import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Play, Radio, Clock, ChevronRight, Goal, Square, AlertTriangle, ArrowRightLeft } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useActiveLiveMatches, useCalculatedMinute, useLiveMatchSubscription, LiveMatch } from '@/hooks/useLiveMatch';
import { useLiveEnabled } from '@/hooks/useLiveEnabled';
import { supabase } from '@/integrations/supabase/client';

interface MatchInfo {
  homeTeam: string;
  awayTeam: string;
  homeAvatar?: string;
  awayAvatar?: string;
}

const LiveMatchCard = ({ liveMatch }: { liveMatch: LiveMatch }) => {
  const { events } = useLiveMatchSubscription(liveMatch.id);
  const currentMinute = useCalculatedMinute(liveMatch);
  const [matchInfo, setMatchInfo] = useState<MatchInfo | null>(null);

  // Fetch match details based on match type
  useEffect(() => {
    const fetchMatchInfo = async () => {
      if (liveMatch.match_type === 'division' || liveMatch.match_type === 'tournament') {
        const { data } = await supabase
          .from('matches')
          .select(`
            team_a_name, team_b_name,
            player_a:players!matches_team_a_fkey(name, avatar),
            player_b:players!matches_team_b_fkey(name, avatar)
          `)
          .eq('id', liveMatch.match_id)
          .single();

        if (data) {
          setMatchInfo({
            homeTeam: (data.player_a as any)?.name || data.team_a_name || 'Home',
            awayTeam: (data.player_b as any)?.name || data.team_b_name || 'Away',
            homeAvatar: (data.player_a as any)?.avatar,
            awayAvatar: (data.player_b as any)?.avatar,
          });
        }
      } else if (liveMatch.match_type === 'world_cup') {
        const { data } = await supabase
          .from('wc_matches')
          .select(`
            team_a:wc_teams!wc_matches_team_a_id_fkey(name, flag_url),
            team_b:wc_teams!wc_matches_team_b_id_fkey(name, flag_url)
          `)
          .eq('id', liveMatch.match_id)
          .single();

        if (data) {
          setMatchInfo({
            homeTeam: (data.team_a as any)?.name || 'Team A',
            awayTeam: (data.team_b as any)?.name || 'Team B',
            homeAvatar: (data.team_a as any)?.flag_url,
            awayAvatar: (data.team_b as any)?.flag_url,
          });
        }
      } else if (liveMatch.match_type === 'external') {
        const { data } = await supabase
          .from('external_matches')
          .select('opponent_club')
          .eq('id', liveMatch.match_id)
          .single();

        if (data) {
          setMatchInfo({
            homeTeam: 'VSU',
            awayTeam: data.opponent_club,
          });
        }
      }
    };

    fetchMatchInfo();
  }, [liveMatch.match_id, liveMatch.match_type]);

  // Get recent events (last 3)
  const recentEvents = events
    .filter(e => !e.is_deleted)
    .slice(-3)
    .reverse();

  const getEventIcon = (type: string) => {
    switch (type) {
      case 'goal':
      case 'penalty_scored':
        return <Goal className="w-3 h-3 text-success" />;
      case 'own_goal':
        return <Goal className="w-3 h-3 text-destructive" />;
      case 'yellow_card':
        return <Square className="w-3 h-3 text-warning fill-warning" />;
      case 'red_card':
        return <Square className="w-3 h-3 text-destructive fill-destructive" />;
      case 'substitution':
        return <ArrowRightLeft className="w-3 h-3 text-muted-foreground" />;
      default:
        return <AlertTriangle className="w-3 h-3" />;
    }
  };

  const getPeriodLabel = (period: string) => {
    const labels: Record<string, string> = {
      'pre_match': 'প্রি-ম্যাচ',
      'first_half': '১ম হাফ',
      'half_time': 'হাফ টাইম',
      'second_half': '২য় হাফ',
      'extra_time_first': 'অতিরিক্ত ১',
      'extra_time_second': 'অতিরিক্ত ২',
      'penalties': 'পেনাল্টি',
      'full_time': 'ফুল টাইম',
    };
    return labels[period] || period;
  };

  const hasStream = (liveMatch as any).stream_url && (liveMatch as any).stream_type !== 'none';

  return (
    <Card className="relative overflow-hidden border-2 border-destructive/50 bg-gradient-to-br from-card via-card to-destructive/5">
      {/* Live Pulse Indicator */}
      <div className="absolute top-3 left-3 flex items-center gap-2">
        <span className="relative flex h-3 w-3">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-destructive opacity-75"></span>
          <span className="relative inline-flex rounded-full h-3 w-3 bg-destructive"></span>
        </span>
        <Badge variant="destructive" className="text-xs font-bold">
          🔴 LIVE
        </Badge>
      </div>

      {/* Period & Time */}
      <div className="absolute top-3 right-3 flex items-center gap-2 text-xs text-muted-foreground">
        <Clock className="w-3 h-3" />
        <span>{currentMinute}'</span>
        <span className="text-muted-foreground/70">|</span>
        <span>{getPeriodLabel(liveMatch.current_period)}</span>
      </div>

      <CardContent className="pt-12 pb-4">
        {/* Score Display */}
        <div className="flex items-center justify-center gap-4 mb-4">
          {/* Home Team */}
          <div className="flex flex-col items-center gap-2 flex-1">
            {matchInfo?.homeAvatar ? (
              <img 
                src={matchInfo.homeAvatar} 
                alt={matchInfo?.homeTeam} 
                className="w-12 h-12 rounded-full object-cover border-2 border-border"
              />
            ) : (
              <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center text-xl font-bold text-primary">
                {matchInfo?.homeTeam?.charAt(0) || 'H'}
              </div>
            )}
            <span className="text-sm font-medium text-center line-clamp-1">
              {matchInfo?.homeTeam || 'Home'}
            </span>
          </div>

          {/* Score */}
          <div className="flex items-center gap-2">
            <span className="text-4xl font-bold text-foreground">{liveMatch.score_home}</span>
            <span className="text-2xl text-muted-foreground">-</span>
            <span className="text-4xl font-bold text-foreground">{liveMatch.score_away}</span>
          </div>

          {/* Away Team */}
          <div className="flex flex-col items-center gap-2 flex-1">
            {matchInfo?.awayAvatar ? (
              <img 
                src={matchInfo.awayAvatar} 
                alt={matchInfo?.awayTeam} 
                className="w-12 h-12 rounded-full object-cover border-2 border-border"
              />
            ) : (
              <div className="w-12 h-12 rounded-full bg-secondary/20 flex items-center justify-center text-xl font-bold text-secondary">
                {matchInfo?.awayTeam?.charAt(0) || 'A'}
              </div>
            )}
            <span className="text-sm font-medium text-center line-clamp-1">
              {matchInfo?.awayTeam || 'Away'}
            </span>
          </div>
        </div>

        {/* Recent Events */}
        {recentEvents.length > 0 && (
          <div className="space-y-1 mb-4 px-2">
            {recentEvents.map((event) => (
              <div key={event.id} className="flex items-center gap-2 text-xs text-muted-foreground">
                {getEventIcon(event.event_type)}
                <span>{event.minute}'</span>
                <span className="truncate">{event.player_name || event.event_type}</span>
              </div>
            ))}
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2">
          {hasStream && (
            <Button asChild className="flex-1" variant="destructive">
              <Link to={`/live/${liveMatch.id}`}>
                <Play className="w-4 h-4 mr-2" />
                লাইভ দেখুন
              </Link>
            </Button>
          )}
          <Button asChild variant={hasStream ? "outline" : "default"} className="flex-1">
            <Link to={`/live/${liveMatch.id}`}>
              <Radio className="w-4 h-4 mr-2" />
              {hasStream ? 'বিস্তারিত' : 'লাইভ দেখুন'}
              <ChevronRight className="w-4 h-4 ml-1" />
            </Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export const LiveMatchWidget = () => {
  const { isEnabled, isLoading: enabledLoading } = useLiveEnabled();
  const { liveMatches, isLoading } = useActiveLiveMatches();

  // Hide widget if live system is disabled
  if (enabledLoading || !isEnabled) {
    return null;
  }

  if (isLoading) {
    return null;
  }

  if (liveMatches.length === 0) {
    return null;
  }

  return (
    <section className="mb-8">
      <div className="flex items-center gap-2 mb-4">
        <Radio className="w-5 h-5 text-destructive animate-pulse" />
        <h2 className="text-xl font-bold">লাইভ ম্যাচ</h2>
        <Badge variant="destructive" className="text-xs">
          {liveMatches.length} লাইভ
        </Badge>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {liveMatches.map((match) => (
          <LiveMatchCard key={match.id} liveMatch={match} />
        ))}
      </div>
    </section>
  );
};
